import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import YhMultiSelect from "../../ui/YhMultiSelect";
import YhSelect from "../../ui/YhSelect";
import {
  YhDateRangePicker,
  YhDateRangeValue,
  formatDdMmYyyy,
} from "../../ui/YhDateTimePicker";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import { getCallingFilters } from "./callingReportService";

export type CallFilters = {
  period: string;
  range: { from: string | null; to: string | null };
  teamIds: Array<string | number>;
  agentIds: Array<string | number>;
  projectIds: Array<string | number>;
  sources: Array<string | number>;
  formIds: Array<string | number>;
  stages: Array<string | number>;
  rowBy: string;
};

type FilterOption = { id: string; label: string };

const todayDmy = () => formatDdMmYyyy(new Date());

const todayRange = (): YhDateRangeValue => {
  const today = todayDmy();
  return { from: today, to: today };
};

export const defaultCallFilters = (): CallFilters => ({
  period: "custom",
  range: todayRange(),
  teamIds: [],
  agentIds: [],
  projectIds: [],
  sources: [],
  formIds: [],
  stages: [],
  rowBy: "salesperson",
});

const csv = (raw?: string | null) =>
  String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const isoToDmy = (value?: string | null) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
};

export const isoFromDmy = (value?: string | null) => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(value || ""));
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
};

const rangeForPeriod = (period: string): YhDateRangeValue => {
  const today = new Date();
  const todayStr = formatDdMmYyyy(today);
  if (period === "week") {
    const mondayOffset = (today.getDay() + 6) % 7;
    const start = new Date(today);
    start.setDate(today.getDate() - mondayOffset);
    return { from: formatDdMmYyyy(start), to: todayStr };
  }
  if (period === "month") {
    return {
      from: formatDdMmYyyy(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: todayStr,
    };
  }
  return { from: todayStr, to: todayStr };
};

export function parseCallFiltersFromSearch(search: string): CallFilters {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const period = q.get("period") || "today";
  const from = isoToDmy(q.get("from"));
  const to = isoToDmy(q.get("to"));
  const range =
    from || to
      ? { from: from || to || todayDmy(), to: to || from || todayDmy() }
      : rangeForPeriod(period);
  return {
    period: "custom",
    range,
    teamIds: csv(q.get("teamIds") || q.get("teamId")),
    agentIds: csv(q.get("agentIds") || q.get("agentId")),
    projectIds: csv(q.get("projectIds")),
    sources: csv(q.get("sources")),
    formIds: csv(q.get("formIds") || q.get("formId")),
    stages: csv(q.get("stages")),
    rowBy: q.get("rowBy") || "salesperson",
  };
}

export function callFiltersToParams(filters: CallFilters) {
  const fallback = todayDmy();
  const from = isoFromDmy(filters.range.from) || isoFromDmy(fallback);
  const to = isoFromDmy(filters.range.to) || from;
  return {
    period: "custom",
    from,
    to,
    rowBy: filters.rowBy,
    teamIds: filters.teamIds.map(String),
    agentIds: filters.agentIds.map(String),
    projectIds: filters.projectIds.map(String),
    sources: filters.sources.map(String),
    formIds: (filters.formIds || []).map(String),
    stages: filters.stages.map(String),
  };
}

const pillControl =
  "[&_button.yh-input]:!min-h-[36px] [&_button.yh-input]:!rounded-full [&_button.yh-input]:!border-0 [&_button.yh-input]:!bg-transparent [&_button.yh-input]:!px-1 [&_button.yh-input]:!py-0 [&_button.yh-input]:!shadow-none";

const FilterPill = ({
  label,
  width,
  children,
}: {
  label: string;
  width?: string;
  children: React.ReactNode;
}) => (
  <div
    className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white pl-3 pr-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    style={width ? { minWidth: width } : undefined}
  >
    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
      {label}
    </span>
    <div className={`min-w-0 flex-1 ${pillControl}`}>{children}</div>
  </div>
);

type Props = {
  value: CallFilters;
  onChange: (next: CallFilters) => void;
  showRowBy?: boolean;
};

const CallFilterBar: React.FC<Props> = ({ value, onChange, showRowBy }) => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [role, setRole] = useState("");
  const [options, setOptions] = useState<{
    agents: FilterOption[];
    teams: FilterOption[];
    projects: FilterOption[];
    sources: FilterOption[];
    forms: FilterOption[];
    stages: FilterOption[];
  }>({ agents: [], teams: [], projects: [], sources: [], forms: [], stages: [] });

  useEffect(() => {
    let cancelled = false;
    getCallingFilters()
      .then((res) => {
        if (cancelled || res?.success === 0) return;
        setRole(String(res?.role || ""));
        setOptions({
          agents: res?.filters?.agents || [],
          teams: res?.filters?.teams || [],
          projects: res?.filters?.projects || [],
          sources: res?.filters?.sources || [],
          forms: res?.filters?.forms || [],
          stages: res?.filters?.stages || [],
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [activeOrganizationId]);

  const patch = (partial: Partial<CallFilters>) => onChange({ ...value, ...partial });
  const canFilterPeople = role !== "agent";
  const toOpts = (rows: FilterOption[]) =>
    rows.map((item) => ({ label: item.label, value: item.id }));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <YhDateRangePicker
        className="!w-[280px] shrink-0"
        placeholder="From - To"
        showShortcuts
        value={value.range}
        onChange={(range) => {
          if (!range.from && !range.to) {
            patch({ period: "custom", range: todayRange() });
            return;
          }
          patch({
            period: "custom",
            range: {
              from: range.from,
              to: range.to || range.from,
            },
          });
        }}
      />
      {canFilterPeople && (
        <FilterPill label="Team" width="180px">
          <YhMultiSelect
            placeholder="All"
            className="!w-full"
            searchable
            showSelectAll
            maxVisibleChips={1}
            value={value.teamIds}
            options={toOpts(options.teams)}
            onChange={(teamIds) => patch({ teamIds })}
          />
        </FilterPill>
      )}
      {canFilterPeople && (
        <FilterPill label="Salesperson" width="200px">
          <YhMultiSelect
            placeholder="All"
            className="!w-full"
            searchable
            showSelectAll
            maxVisibleChips={1}
            value={value.agentIds}
            options={toOpts(options.agents)}
            onChange={(agentIds) => patch({ agentIds })}
          />
        </FilterPill>
      )}
      <FilterPill label="Project" width="180px">
        <YhMultiSelect
          placeholder="All"
          className="!w-full"
          searchable
          showSelectAll
          maxVisibleChips={1}
          value={value.projectIds}
          options={toOpts(options.projects)}
          onChange={(projectIds) => patch({ projectIds })}
        />
      </FilterPill>
      <FilterPill label="Source" width="180px">
        <YhMultiSelect
          placeholder="All"
          className="!w-full"
          searchable
          showSelectAll
          maxVisibleChips={1}
          value={value.sources}
          options={toOpts(options.sources)}
          onChange={(sources) => patch({ sources })}
        />
      </FilterPill>
      <FilterPill label="Form" width="200px">
        <YhMultiSelect
          placeholder="All"
          className="!w-full"
          searchable
          showSelectAll
          maxVisibleChips={1}
            value={value.formIds || []}
          options={toOpts(options.forms)}
          onChange={(formIds) => patch({ formIds })}
        />
      </FilterPill>
      <FilterPill label="Call stage" width="200px">
        <YhMultiSelect
          placeholder="All"
          className="!w-full"
          searchable
          showSelectAll
          maxVisibleChips={1}
          value={value.stages}
          options={toOpts(options.stages)}
          onChange={(stages) => patch({ stages })}
        />
      </FilterPill>
      {showRowBy && (
        <FilterPill label="Group by" width="180px">
          <YhSelect
            placeholder="Salesperson"
            className="!w-full"
            value={value.rowBy}
            options={[
              { label: "Salesperson", value: "salesperson" },
              { label: "Team", value: "team" },
              { label: "Project", value: "project" },
              { label: "Source", value: "source" },
              { label: "Form", value: "form" },
            ]}
            onChange={(event) =>
              patch({ rowBy: String(event.target.value || "salesperson") })
            }
          />
        </FilterPill>
      )}
    </div>
  );
};

export default CallFilterBar;
